import * as React from 'react'
import cn from 'classnames'

// Shared field styling + time/timezone helpers for the notification modal.
// Copied from the check-in cycle wizard so the option lists and parsing stay in
// sync; lives here so both cycle types can use one source.

// Figma: label is TT Commons 16px / 16px line-height, Regular, gray-500.
export const fieldLabelClass =
  'mb-2 block text-base font-normal leading-4 text-[#5E6580]'

// Figma: inputs/dropdowns are 44px tall (h-11), rounded-sm, slate-100 fill,
// 16px text. slate-100 maps to the working light-grey fill this app already uses.
export const baseInputClass =
  'h-11 w-full rounded-sm border-0 bg-[#EFF1F8] px-4 text-base text-[#1D264A] placeholder:text-[#9aa1ba] focus:outline-none focus:ring-2'

export const inputClass = (hasError: boolean): string =>
  cn(
    baseInputClass,
    hasError ? 'ring-2 ring-[#E42309]' : 'focus:ring-[#2E9E5D]',
  )

export const FieldError = ({ message }: { message: string | null }) =>
  message ? (
    <p className="mt-1.5 text-[13px] text-[#E42309]">{message}</p>
  ) : null

// --- Offset + subject validation -------------------------------------------
// Mirror CheckinCycleNotification's server-side guards so the modal blocks
// before a round-trip. Shared by both entry points (the check-in cycle wizard
// and the Performance Cycle defaults page) since they use this one modal.

// Cap the before/after-event offset. The column is a 4-byte integer, so a year
// of lead time is far more than any real reminder needs and it stops a giant
// input (e.g. 1e+203) from overflowing the column server-side.
export const OFFSET_DAYS_MAX = 365

// Coerce raw number-input text into an in-range whole number of days. Empty /
// non-numeric input (or a value below 0) becomes 0; anything over the cap is
// clamped down, so the field can never hold a scientific-notation blow-up.
export const clampOffsetDays = (raw: string): number => {
  const value = Math.floor(Number(raw))
  if (!Number.isFinite(value)) return 0
  return Math.min(OFFSET_DAYS_MAX, Math.max(0, value))
}

// Mirror CheckinCycleNotification::{NAME,SUBJECT}_MAX_LENGTH, which in turn
// match CheckinCycle::NAME_MAX_LENGTH — one cap for every admin-authored label
// in this feature.
export const NAME_MAX_LENGTH = 255
export const SUBJECT_MAX_LENGTH = 255

// A subject must contain at least one letter — a value made up solely of
// punctuation/symbols ("!@#$") is not a meaningful subject. `\p{L}` is
// Unicode-aware, so accented subjects pass; digits alone do not.
export const hasLetter = (value: string): boolean => /\p{L}/u.test(value)

// Inline error for the subject field, or null when valid. `submitAttempted`
// gates the "required" message so it only shows after a save try, while the
// too-long / letterless messages surface as soon as they apply.
export const subjectError = (
  value: string,
  submitAttempted: boolean,
): string | null => {
  const trimmed = value.trim()
  if (trimmed === '') return submitAttempted ? 'Subject is required' : null
  if (trimmed.length > SUBJECT_MAX_LENGTH) {
    return `Subject must be ${SUBJECT_MAX_LENGTH} characters or fewer`
  }
  if (!hasLetter(trimmed)) return 'Subject must include at least one letter'
  return null
}

// Inline error for the notification name field, or null when valid.
// `submitAttempted` gates the "required" message so it only shows after a save
// try, while the too-long / letterless / duplicate messages surface as soon as
// they apply. `existingNames` holds the OTHER reminders' names in the same list,
// compared ignoring case and surrounding whitespace. Mirrors
// CheckinCycleNotification's name validations on the server.
export const notificationNameError = (
  value: string,
  submitAttempted: boolean,
  existingNames: string[] = [],
): string | null => {
  const trimmed = value.trim()
  if (trimmed === '')
    return submitAttempted ? 'Notification name is required' : null
  if (trimmed.length > NAME_MAX_LENGTH) {
    return `Notification name must be ${NAME_MAX_LENGTH} characters or fewer`
  }
  if (!hasLetter(trimmed)) {
    return 'Notification name must include at least one letter'
  }
  if (
    existingNames.some(
      (existing) => existing.trim().toLowerCase() === trimmed.toLowerCase(),
    )
  ) {
    return 'A reminder with this name already exists'
  }
  return null
}

// Recipient email validation. The field accepts one or more comma-separated
// addresses; every non-empty entry must be a well-formed address, and there
// must be at least one (so a blank / whitespace-only value is invalid).
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const parseEmailList = (raw: string): string[] =>
  raw
    .split(',')
    .map((email) => email.trim())
    .filter((email) => email !== '')

export const areEmailsValid = (raw: string): boolean => {
  const list = parseEmailList(raw)
  if (list.length === 0 || !list.every((email) => EMAIL_RE.test(email))) {
    return false
  }

  const unique = new Set(list.map((email) => email.toLowerCase()))
  return unique.size === list.length
}

// Distinguishes *why* areEmailsValid failed, for a more specific inline message
// than a generic "invalid" — in particular, a well-formed but repeated address.
export const hasDuplicateEmails = (raw: string): boolean => {
  const list = parseEmailList(raw)
  const unique = new Set(list.map((email) => email.toLowerCase()))
  return unique.size !== list.length
}

// Rich-text (HTML) blank check: a whitespace-only editor body still carries
// `<p>…</p>` markup (and can contain `&nbsp;`), so strip tags + non-breaking
// spaces before trimming. Returns false for empty / whitespace-only bodies.
export const htmlHasContent = (html: string): boolean =>
  html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim() !== ''

// Display labels for the time selects: "12 AM" … "11 PM".
export const TIME_OPTIONS = Array.from({ length: 24 }, (_, hour) => {
  const period = hour < 12 ? 'AM' : 'PM'
  const display = hour % 12 === 0 ? 12 : hour % 12
  return `${display} ${period}`
})

// "9 AM" / "12 PM" → "HH:00" (the form the `time` column parses cleanly).
export const toTwentyFourHour = (label: string): string => {
  const match = label.match(/^(\d{1,2})\s*(AM|PM)$/i)
  if (!match) return label

  let hour = Number(match[1]) % 12
  if (match[2].toUpperCase() === 'PM') hour += 12

  return `${hour.toString().padStart(2, '0')}:00`
}

// Stored time (ISO datetime or "HH:MM[:SS]") → "9 AM" / "12 PM" label. Defaults 9 AM.
export const labelFromStoredTime = (value: string | null): string => {
  const match = value?.match(/(?:T|^)(\d{1,2}):(\d{2})/)
  if (!match) return '9 AM'

  const hour24 = Number(match[1])
  const period = hour24 < 12 ? 'AM' : 'PM'
  const display = hour24 % 12 === 0 ? 12 : hour24 % 12
  return `${display} ${period}`
}

// A broad IANA timezone picker.
export const TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time — New York (EST/EDT)' },
  { value: 'America/Chicago', label: 'Central Time — Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'Mountain Time — Denver (MST/MDT)' },
  {
    value: 'America/Los_Angeles',
    label: 'Pacific Time — Los Angeles (PST/PDT)',
  },
  { value: 'America/Anchorage', label: 'Alaska Time — Anchorage (AKST/AKDT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time — Honolulu (HST)' },
  { value: 'America/Toronto', label: 'Toronto (ET)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'America/Mexico_City', label: 'Mexico City (CT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Dublin', label: 'Dublin (GMT/IST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
  { value: 'Europe/Athens', label: 'Athens (EET/EEST)' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
  { value: 'Africa/Lagos', label: 'Lagos (WAT)' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India — Kolkata (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
]
